import { useI18n } from '@/i18n'
import { Funnel } from '@/shared/icons/phosphor'
import { type JSX, Show, createEffect, createSignal, createUniqueId, splitProps } from 'solid-js'
import { Badge } from '../../primitives/Badge'
import { Checkbox } from '../../primitives/Checkbox'
import { createScrollEdges } from '../../primitives/ScrollArea/createScrollEdges'
import { Popover } from '../Popover'

export type FilterControlSize = 'md' | 'lg'

export type FilterPopoverWidth = 'md' | 'lg'

const controlSize: Record<FilterControlSize, string> = {
  md: 'h-10 rounded-[var(--radius-12)]',
  lg: 'h-12 rounded-[var(--radius-16)]',
}

const popoverWidth: Record<FilterPopoverWidth, string> = {
  md: 'w-[320px]',
  lg: 'w-[calc(100vw-32px)] sm:w-[560px] lg:w-[760px]',
}

const controlBase =
  'inline-flex items-center gap-1.5 border text-[length:var(--text-sm)] font-semibold transition-all duration-[var(--duration-fast)] whitespace-nowrap focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-primary-500)]'
const controlInactive =
  'border-[var(--color-border)] text-[color:var(--color-text-muted)] bg-[var(--color-surface)] hover:border-[var(--color-neutral-300)] hover:text-[color:var(--color-text)]'
const controlActive =
  'bg-[var(--color-primary-50)] border-[var(--color-primary-200)] text-[color:var(--color-primary-700)]'

export function FilterChevron() {
  return (
    <svg
      class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[color:var(--color-text-muted)]"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  )
}

export interface ToolbarSelectProps
  extends Omit<JSX.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: FilterControlSize
}

export function ToolbarSelect(props: ToolbarSelectProps) {
  let selectRef: HTMLSelectElement | undefined
  const [local, rest] = splitProps(props, ['size', 'class', 'children', 'value'])

  createEffect(() => {
    const value = local.value
    void local.children
    if (selectRef && value !== undefined) {
      selectRef.value = String(value)
    }
  })

  return (
    <div class={`relative ${local.class ?? ''}`}>
      <select
        {...rest}
        ref={selectRef}
        value={local.value === undefined ? undefined : String(local.value)}
        class={`${controlBase} ${controlInactive} ${controlSize[local.size ?? 'md']} w-full appearance-none overflow-hidden text-ellipsis pl-3 pr-7 cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-primary-500)]`}
      >
        {local.children}
      </select>
      <FilterChevron />
    </div>
  )
}

export function toolbarButtonClass(size: FilterControlSize = 'md'): string {
  return `${controlBase} ${controlInactive} ${controlSize[size]} justify-center px-3 cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-primary-500)] aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:border-[var(--color-border)] aria-disabled:hover:text-[color:var(--color-text-muted)]`
}

export interface FilterPopoverProps {
  activeCount: number
  onClear: () => void
  children: JSX.Element
  size?: FilterControlSize
  width?: FilterPopoverWidth
  label?: string
  clearLabel?: string
  iconOnlyWhenNarrow?: boolean
}

function FilterPopoverBody(props: { children: JSX.Element }) {
  const edges = createScrollEdges('y')

  return (
    <div class="relative flex min-h-0 flex-1 flex-col">
      <div ref={edges.attach} class="relative min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        <div ref={edges.observe} class="flex flex-col gap-3">
          {props.children}
        </div>
      </div>
      <Show when={edges.end()}>
        <div
          aria-hidden="true"
          class="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-[var(--color-surface)] to-transparent"
        />
      </Show>
    </div>
  )
}

export function FilterPopover(props: FilterPopoverProps) {
  const { t } = useI18n()
  const [open, setOpen] = createSignal(false)
  let panelRef: HTMLElement | undefined

  const label = () => props.label ?? t('filters.title')
  const active = () => props.activeCount > 0

  const focusPanel = (event: Event) => {
    event.preventDefault()
    panelRef?.focus({ preventScroll: true })
  }

  return (
    <Popover
      open={open()}
      onOpenChange={setOpen}
      placement="bottom-end"
      gutter={4}
      overflowPadding={16}
    >
      <Popover.Trigger
        class={`${controlBase} ${controlSize[props.size ?? 'md']} px-3 cursor-pointer ${
          active() ? controlActive : controlInactive
        }`}
        aria-label={label()}
      >
        <Funnel class="w-4 h-4" weight={active() ? 'fill' : 'regular'} />
        <span class={props.iconOnlyWhenNarrow ? '@max-xl:sr-only' : undefined}>{label()}</span>
        <Show when={active()}>
          <Badge variant="primary" size="sm" class="shrink-0">
            {props.activeCount}
          </Badge>
        </Show>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          ref={(el: HTMLElement) => {
            panelRef = el
          }}
          tabIndex={-1}
          onOpenAutoFocus={focusPanel}
          class={`z-50 ${popoverWidth[props.width ?? 'md']} flex flex-col overflow-hidden max-h-[var(--kb-popper-content-available-height)] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-float)] origin-top-right animate-[dropdown-enter_150ms_var(--ease-component)] focus-visible:outline-none`}
        >
          <div class="flex shrink-0 items-center justify-between px-4 pt-4 pb-3">
            <span class="text-[length:var(--text-sm)] font-bold text-[color:var(--color-text)]">
              {label()}
            </span>
            <Show when={active()}>
              <button
                type="button"
                onClick={() => props.onClear()}
                class="-mr-1.5 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[length:var(--text-xs)] font-semibold text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)] hover:underline cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-primary-500)]"
              >
                {props.clearLabel ?? t('filters.clearAll')}
              </button>
            </Show>
          </div>
          <FilterPopoverBody>{props.children}</FilterPopoverBody>
        </Popover.Content>
      </Popover.Portal>
    </Popover>
  )
}

export interface FilterSectionProps {
  label: string
  children: JSX.Element
}

export function FilterSection(props: FilterSectionProps) {
  return (
    <div>
      <span class="block text-[length:var(--text-xs)] font-semibold text-[color:var(--color-text-muted)] mb-1">
        {props.label}
      </span>
      {props.children}
    </div>
  )
}

export interface FilterChipProps {
  selected: boolean
  onClick: () => void
  children: JSX.Element
}

export function FilterChip(props: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={() => props.onClick()}
      aria-pressed={props.selected}
      class={`inline-flex items-center gap-1.5 h-8 px-3 rounded-[var(--radius-md)] border text-[length:var(--text-sm)] font-semibold transition-all duration-[var(--duration-fast)] cursor-pointer ${
        props.selected ? controlActive : controlInactive
      }`}
    >
      {props.children}
    </button>
  )
}

export interface FilterFieldSelectProps
  extends Omit<JSX.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {}

export function FilterFieldSelect(props: FilterFieldSelectProps) {
  let selectRef: HTMLSelectElement | undefined
  const [local, rest] = splitProps(props, ['class', 'children', 'value'])

  createEffect(() => {
    const value = local.value
    void local.children
    if (selectRef && value !== undefined) {
      selectRef.value = String(value)
    }
  })

  return (
    <div class={`relative ${local.class ?? ''}`}>
      <select
        {...rest}
        ref={selectRef}
        value={local.value === undefined ? undefined : String(local.value)}
        class="w-full h-9 pl-2.5 pr-7 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] text-[length:var(--text-sm)] text-[color:var(--color-text)] appearance-none cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-primary-500)]"
      >
        {local.children}
      </select>
      <FilterChevron />
    </div>
  )
}

export interface FilterCheckRowProps {
  checked: boolean
  onChange: () => void
  label: JSX.Element
  count?: number
  dense?: boolean
}

export function FilterCheckRow(props: FilterCheckRowProps) {
  const inputId = createUniqueId()

  return (
    <div
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          props.onChange()
        }
      }}
      class="flex items-center px-1.5 rounded-[var(--radius-md)] cursor-pointer hover:bg-[var(--color-neutral-100)]"
    >
      <Checkbox id={inputId} size="sm" checked={props.checked} onChange={() => props.onChange()} />
      <label
        for={inputId}
        class={`flex min-w-0 flex-1 items-center gap-2 pl-2 cursor-pointer ${
          props.dense ? 'py-1' : 'py-1.5'
        }`}
      >
        <span
          class={`flex-1 min-w-0 truncate ${
            props.dense
              ? 'text-[length:var(--text-xs)] text-[color:var(--color-text-muted)]'
              : 'text-[length:var(--text-sm)] text-[color:var(--color-text)]'
          }`}
        >
          {props.label}
        </span>
        <Show when={props.count !== undefined}>
          <span class="tabular-nums text-[length:var(--text-xs)] text-[color:var(--color-text-muted)]">
            {props.count}
          </span>
        </Show>
      </label>
    </div>
  )
}

export interface FilterScrollListProps {
  maxHeight: string
  children: JSX.Element
}

export function FilterScrollList(props: FilterScrollListProps) {
  return (
    <div
      class="overflow-y-auto flex flex-col gap-0.5 -mx-1 px-1"
      style={{ 'max-height': props.maxHeight }}
    >
      {props.children}
    </div>
  )
}
